import * as React from 'react';
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  Printer, Filter, X, ArrowUpRight, ArrowDownRight, User2, Calendar, Tag, Receipt, Search,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import * as Dialog from '@/components/ui/dialog';
import { DateRangePicker } from '@/components/date-range-picker';
import { RefreshCacheButton } from '@/components/refresh-cache-button';
import { useSalesFilters } from '@/lib/sales-filters';
import { getScanSummary, type ScanSummaryRow } from '@/lib/sales-api';
import { fmtINR, fmtNumber, fmtDate } from '@/lib/format';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_app/sales/estimates')({ component: Page });

type EstType = 'ALL' | 'S' | 'P';

interface PageFilters {
  ttype: EstType;
  brand: string | null;
  artNo: string | null;
  /** Exact POS username who made the scan. */
  posUser: string | null;
  /** Free-text query — substring match across barcode/item/brand/artNo/color/size. */
  q: string | null;
}

function usePageFilters() {
  const search = useRouterState({
    select: (s) => s.location.search as {
      ttype?: EstType; brand?: string; artNo?: string; q?: string; posUser?: string },
  });
  const nav = useNavigate();
  const filters: PageFilters = {
    ttype: search.ttype ?? 'ALL',
    brand: search.brand ?? null,
    artNo: search.artNo ?? null,
    posUser: search.posUser ?? null,
    q: search.q ?? null,
  };
  const setFilters = React.useCallback((patch: Partial<PageFilters>) => {
    nav({
      to: '.',
      search: (prev) => {
        const next = { ...(prev as Record<string, unknown>) };
        for (const [k, v] of Object.entries(patch)) {
          if (v === null || v === '' || v === 'ALL') delete next[k];
          else next[k] = v;
        }
        return next;
      },
      replace: true,
    });
  }, [nav]);
  return { ...filters, setFilters };
}

function applyClientFilters(rows: ScanSummaryRow[], f: PageFilters): ScanSummaryRow[] {
  const q = f.q?.trim().toLowerCase() ?? '';
  if (!f.brand && !f.artNo && !q) return rows;
  return rows.filter((r) => {
    if (f.brand && r.brand !== f.brand) return false;
    if (f.artNo && r.artNo !== f.artNo) return false;
    if (q) {
      const hay = (
        r.barcode + ' ' + r.itemName + ' ' + r.brand + ' ' + r.artNo + ' ' +
        r.color + ' ' + r.size + ' ' + r.salesmanName + ' ' + r.posUser
      ).toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

function deriveOptions(rows: ScanSummaryRow[]) {
  const brands = new Set<string>();
  const artNos = new Set<string>();
  for (const r of rows) {
    if (r.brand) brands.add(r.brand);
    if (r.artNo) artNos.add(r.artNo);
  }
  return {
    brands: [...brands].sort(),
    artNos: [...artNos].sort(),
  };
}

// ────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────
function Page() {
  const { from, to, store, setFilters: setShared } = useSalesFilters();
  const { ttype, brand, artNo, q, posUser, setFilters } = usePageFilters();
  const [filterOpen, setFilterOpen] = React.useState(false);
  const [showCount, setShowCount] = React.useState(200);

  const query = useQuery({
    queryKey: ['sales:scan-summary', from, to, store, ttype, posUser],
    queryFn: () => getScanSummary({ from, to, store, type: ttype, posUser }),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });

  const allRows = query.data?.rows ?? [];
  // posUser is applied server-side (it also drives which rows the dropdown is
  // built from), so the client-side pass leaves it alone.
  const filterState: PageFilters = { ttype, brand, artNo, q, posUser };
  const filtered = React.useMemo(() => applyClientFilters(allRows, filterState), [allRows, brand, artNo, q]);
  const options = React.useMemo(() => deriveOptions(allRows), [allRows]);

  // Recompute totals from the filtered set so the headline updates live.
  const totals = React.useMemo(() => {
    let saleCount = 0, saleAmt = 0, purCount = 0, purAmt = 0;
    for (const r of filtered) {
      if (r.trantype === 'S') { saleCount += 1; saleAmt += r.sp; }
      else if (r.trantype === 'P') { purCount += 1; purAmt += r.sp; }
    }
    return { saleCount, saleAmt, purCount, purAmt };
  }, [filtered]);

  React.useEffect(() => { setShowCount(200); }, [from, to, store, ttype, brand, artNo, q]);
  const visibleRows = filtered.slice(0, showCount);
  const activeFilterCount = [brand, artNo, q].filter(Boolean).length + (ttype !== 'ALL' ? 1 : 0);

  return (
    <div className="relative space-y-6 pb-24 print:space-y-4 print:pb-0">
      {query.isFetching && (
        <div className="pointer-events-none fixed inset-x-0 top-0 z-50 h-0.5 print:hidden">
          <div className="h-full w-full origin-left animate-[indeterminate_1.4s_ease-in-out_infinite] bg-copper" />
        </div>
      )}

      {/* Header */}
      <header className="flex flex-wrap items-end justify-between gap-3 print:mb-2">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            Daleep · Sales
          </p>
          <h1 className="font-display text-3xl font-medium tracking-tight text-ink sm:text-4xl">
            Barcode Scan Summary
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {fmtDate(from)} → {fmtDate(to)}
            {store ? <span className="text-ink"> · {store}</span> : ' · all stores'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 print:hidden">
          <RefreshCacheButton
            from={from} to={to} store={store} which="estimate"
            invalidateKeys={[['sales:scan-summary']]}
          />
          <Button variant="outline" size="sm" onClick={() => setFilterOpen(true)} className="lg:hidden">
            <Filter className="mr-2 h-4 w-4" /> Filters
            {activeFilterCount > 0 && (
              <span className="ml-2 rounded-full bg-copper px-1.5 text-[10px] font-bold text-white">
                {activeFilterCount}
              </span>
            )}
          </Button>
          <Button size="sm" onClick={() => window.print()} className="bg-ink text-white hover:bg-ink/90">
            <Printer className="mr-2 h-4 w-4" /> Download PDF
          </Button>
        </div>
      </header>

      {/* Type segmented control — primary axis. Mirrors legacy ALL / SALE / PURCHASE radio. */}
      <div
        role="radiogroup"
        aria-label="Scan type"
        className="flex w-full items-center gap-1 rounded-full border border-border/80 bg-sand/40 p-1 sm:w-auto sm:max-w-md print:hidden"
      >
        {(['ALL', 'S', 'P'] as const).map((t) => {
          const selected = ttype === t;
          const labelMap = { ALL: 'All', S: 'Sale', P: 'Purchase' } as const;
          return (
            <button
              key={t}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => setFilters({ ttype: t })}
              className={cn(
                'relative flex-1 rounded-full px-4 py-2 text-sm font-semibold transition-all',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-copper',
                selected
                  ? 'bg-ink text-white shadow-md ring-1 ring-copper/40'
                  : 'text-muted-foreground hover:bg-white/60 hover:text-ink'
              )}
            >
              {labelMap[t]}
            </button>
          );
        })}
      </div>

      {/* Filter bar (desktop) */}
      <div className="hidden lg:block print:hidden">
        <FilterBar
          from={from} to={to}
          onDate={(r) => setShared({ from: r.from, to: r.to })}
          brand={brand} artNo={artNo} q={q}
          options={options}
          onChange={setFilters}
        />
      </div>
      {(brand || artNo || q) && (
        <div className="flex flex-wrap gap-1.5 lg:hidden print:hidden">
          {q && <Chip onRemove={() => setFilters({ q: null })}>Search: {q}</Chip>}
          {brand && <Chip onRemove={() => setFilters({ brand: null })}>Brand: {brand}</Chip>}
          {artNo && <Chip onRemove={() => setFilters({ artNo: null })}>Art: {artNo}</Chip>}
        </div>
      )}

      {query.isLoading && !query.data && <SkeletonHero />}
      {query.error && (
        <p className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {(query.error as Error).message}
        </p>
      )}

      {query.data && (
        <div className={cn('space-y-6 transition-opacity', query.isFetching && 'opacity-60')}>
          <KpiHero
            saleCount={totals.saleCount} saleAmt={totals.saleAmt}
            purCount={totals.purCount} purAmt={totals.purAmt}
          />

          <section aria-label="Detail rows" className="space-y-3">
            <div className="flex items-baseline justify-between">
              <SectionTitle>Scans · {fmtNumber(filtered.length)} rows</SectionTitle>
              <p className="text-xs text-muted-foreground">
                Showing {Math.min(showCount, filtered.length)} of {fmtNumber(filtered.length)}
              </p>
            </div>
            <DetailTable rows={visibleRows} />
            <DetailCards rows={visibleRows} />
            {filtered.length > showCount && (
              <div className="flex justify-center print:hidden">
                <Button variant="outline" size="sm" onClick={() => setShowCount((c) => c + 500)}>
                  Show 500 more
                </Button>
              </div>
            )}
          </section>
        </div>
      )}

      {/* Mobile filter sheet */}
      <Dialog.Root open={filterOpen} onOpenChange={setFilterOpen}>
        <Dialog.Content className="max-h-[85vh] overflow-y-auto sm:max-w-md">
          <Dialog.Title className="font-display text-xl">Filters</Dialog.Title>
          <FilterBar
            from={from} to={to}
            onDate={(r) => setShared({ from: r.from, to: r.to })}
            brand={brand} artNo={artNo} q={q} posUser={posUser}
            posUsers={query.data?.posUsers ?? []}
            options={options}
            onChange={setFilters}
            stacked
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="ghost" size="sm"
              onClick={() => { setFilters({ brand: null, artNo: null, q: null, posUser: null, ttype: 'ALL' }); setFilterOpen(false); }}
            >
              Clear all
            </Button>
            <Button size="sm" onClick={() => setFilterOpen(false)}>Apply</Button>
          </div>
        </Dialog.Content>
      </Dialog.Root>

      <style>{`
        @keyframes indeterminate {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(0%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// FilterBar — date + free-text search + brand/article selects.
// Free-text search subsumes the legacy report's per-field boxes (BARCODE,
// ITEM NAME, COLOR, SIZE, MRP, USERNAME, …) — one input does substring
// match across all of them, which is faster to use than picking the right
// field per query.
// ────────────────────────────────────────────────────────────
function FilterBar({
  from, to, onDate, brand, artNo, q, posUser, posUsers = [], options, onChange, stacked = false,
}: {
  from: string; to: string;
  onDate: (r: { from: string; to: string }) => void;
  brand: string | null; artNo: string | null; q: string | null;
  posUser?: string | null;
  posUsers?: string[];
  options: { brands: string[]; artNos: string[] };
  onChange: (patch: Partial<PageFilters>) => void;
  stacked?: boolean;
}) {
  // Local-state debounce so each keystroke doesn't push a URL event.
  const [draft, setDraft] = React.useState(q ?? '');
  React.useEffect(() => setDraft(q ?? ''), [q]);
  React.useEffect(() => {
    const t = setTimeout(() => {
      const next = draft.trim();
      if (next !== (q ?? '')) onChange({ q: next || null });
    }, 200);
    return () => clearTimeout(t);
  }, [draft, q, onChange]);

  return (
    <div className={cn(
      'rounded-xl border border-border/80 bg-sand/50 p-3 shadow-sm',
      stacked ? 'space-y-3' : 'flex flex-wrap items-end gap-2'
    )}>
      <div className={cn(stacked ? '' : 'min-w-[260px]')}>
        <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          Date range
        </label>
        <DateRangePicker from={from} to={to} onChange={onDate} />
      </div>
      <div className={cn(stacked ? '' : 'min-w-[240px] flex-1')}>
        <label className="mb-1 flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          <Search className="h-3.5 w-3.5" /> Search
        </label>
        <div className="relative">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Barcode, item, brand, art, color, size, user…"
            className="h-10 pr-8"
          />
          {draft && (
            <button
              type="button"
              onClick={() => setDraft('')}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-ink"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
      <NativeSelect
        label="Brand" icon={<Tag className="h-3.5 w-3.5" />}
        value={brand ?? ''}
        onChange={(v) => onChange({ brand: v || null })}
        placeholder="All brands"
        options={options.brands.map((b) => ({ value: b, label: b }))}
        stacked={stacked}
      />
      <NativeSelect
        label="POS user" icon={<Tag className="h-3.5 w-3.5" />}
        value={posUser ?? ''}
        onChange={(v) => onChange({ posUser: v || null })}
        placeholder="All users"
        options={posUsers.map((u) => ({ value: u, label: u }))}
        stacked={stacked}
      />
      <NativeSelect
        label="Article" icon={<Receipt className="h-3.5 w-3.5" />}
        value={artNo ?? ''}
        onChange={(v) => onChange({ artNo: v || null })}
        placeholder="All articles"
        options={options.artNos.map((a) => ({ value: a, label: a }))}
        stacked={stacked}
      />
    </div>
  );
}

function NativeSelect({
  label, icon, value, onChange, placeholder, options, stacked,
}: {
  label: string;
  icon?: React.ReactNode;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  options: { value: string; label: string }[];
  stacked?: boolean;
}) {
  return (
    <div className={cn(stacked ? '' : 'min-w-[180px]')}>
      <label className="mb-1 flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {icon} {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
      >
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// KPI Hero — Sale (TRANTYPE='S') vs Purchase (TRANTYPE='P').
// ────────────────────────────────────────────────────────────
function KpiHero({
  saleCount, saleAmt, purCount, purAmt,
}: {
  saleCount: number; saleAmt: number; purCount: number; purAmt: number;
}) {
  const netCount = saleCount - purCount;
  const netAmt = saleAmt - purAmt;
  return (
    <section className="overflow-hidden rounded-2xl border border-border/80 bg-gradient-to-br from-sand to-white p-4 shadow-sm sm:p-6 print:break-inside-avoid print:bg-white print:shadow-none">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 sm:gap-6">
        <HeroTile label="Net amount" value={fmtINR(netAmt)} accent="copper" big />
        <HeroTile label="Net qty" value={fmtNumber(netCount)} big />
        <HeroTile
          label="Sale" value={fmtINR(saleAmt)}
          hint={`${fmtNumber(saleCount)} items`}
          icon={<ArrowUpRight className="h-3.5 w-3.5 text-emerald-700" />}
        />
        <HeroTile
          label="Purchase" value={fmtINR(purAmt)}
          hint={`${fmtNumber(purCount)} items`}
          accent="rose"
          icon={<ArrowDownRight className="h-3.5 w-3.5 text-rose-deep" />}
        />
      </div>
    </section>
  );
}

function HeroTile({
  label, value, hint, icon, accent, big,
}: {
  label: string; value: string; hint?: string;
  icon?: React.ReactNode;
  accent?: 'copper' | 'rose';
  big?: boolean;
}) {
  const accentClass =
    accent === 'copper' ? 'text-copper'
    : accent === 'rose' ? 'text-rose-deep'
    : 'text-ink';
  return (
    <div className="space-y-1">
      <p className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
        {icon}{label}
      </p>
      <p className={cn(
        'font-display font-medium leading-[1.05]',
        big ? 'text-3xl sm:text-5xl' : 'text-2xl sm:text-3xl',
        accentClass,
      )}>
        {value}
      </p>
      {hint && <p className="font-mono-num text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Detail table + mobile cards
// ────────────────────────────────────────────────────────────
function DetailTable({ rows }: { rows: ScanSummaryRow[] }) {
  return (
    <div className="hidden overflow-x-auto rounded-lg border border-border/70 bg-background sm:block print:block print:rounded-none print:border-black">
      <table className="w-full text-[12px]">
        <thead className="bg-sand/70 text-[10px] uppercase tracking-wider text-muted-foreground print:bg-white">
          <tr>
            <Th>Date</Th><Th>Time</Th><Th>Barcode</Th>
            <Th>Item</Th><Th>Brand</Th><Th>Art No.</Th>
            <Th>Color</Th><Th>Size</Th>
            <Th align="right">SP</Th>
            <Th>Type</Th><Th>User</Th><Th>Salesman</Th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={12} className="p-8 text-center text-sm text-muted-foreground">
                No scans for the current filter.
              </td>
            </tr>
          )}
          {rows.map((r, i) => (
            <tr
              key={`${r.barcode}-${r.date}-${r.time}-${r.trantype}-${i}`}
              className={cn(
                'border-t border-border/40 transition-colors hover:bg-sand/40',
                r.trantype === 'P' && 'bg-rose-soft/40',
              )}
            >
              <Td className="font-mono-num text-muted-foreground">{fmtDate(r.date)}</Td>
              <Td className="font-mono-num text-muted-foreground">{r.time}</Td>
              <Td className="font-mono-num text-[11px]">{r.barcode}</Td>
              <Td>{r.itemName}</Td>
              <Td className="text-muted-foreground">{r.brand}</Td>
              <Td className="font-mono-num">{r.artNo}</Td>
              <Td className="text-muted-foreground">{r.color}</Td>
              <Td className="font-mono-num">{r.size}</Td>
              <Td align="right" className={cn(
                'font-mono-num font-semibold',
                r.trantype === 'P' ? 'text-rose-deep' : 'text-ink',
              )}>
                {r.trantype === 'P' ? `-${fmtINR(r.sp)}` : fmtINR(r.sp)}
              </Td>
              <Td>
                <span className={cn(
                  'inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                  r.trantype === 'P'
                    ? 'bg-rose-soft text-rose-deep'
                    : 'bg-copper-soft text-copper',
                )}>
                  {r.trantype === 'P' ? 'PURCHASE' : 'SALE'}
                </span>
              </Td>
              <Td className="font-mono-num text-[11px] text-muted-foreground">{r.posUser}</Td>
              <Td className="font-mono-num text-[11px] text-muted-foreground">
                {r.salesmanName || r.salesmanId || '—'}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th className={cn(
      'whitespace-nowrap px-2.5 py-2 font-medium',
      align === 'right' ? 'text-right' : 'text-left',
    )}>
      {children}
    </th>
  );
}
function Td({ children, align = 'left', className }: { children: React.ReactNode; align?: 'left' | 'right'; className?: string }) {
  return (
    <td className={cn(
      'whitespace-nowrap px-2.5 py-1.5',
      align === 'right' ? 'text-right' : 'text-left',
      className,
    )}>
      {children}
    </td>
  );
}

function DetailCards({ rows }: { rows: ScanSummaryRow[] }) {
  return (
    <div className="space-y-2 sm:hidden print:hidden">
      {rows.length === 0 && (
        <p className="rounded-lg border border-dashed border-border/60 p-6 text-center text-sm text-muted-foreground">
          No scans for the current filter.
        </p>
      )}
      {rows.map((r, i) => (
        <div
          key={`${r.barcode}-${r.date}-${r.time}-${r.trantype}-${i}`}
          className={cn(
            'rounded-lg border border-border/70 bg-background p-3 shadow-sm',
            r.trantype === 'P' && 'border-rose-deep/30 bg-rose-soft/30',
          )}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-ink">{r.itemName}</p>
              <p className="font-mono-num text-[11px] text-muted-foreground">
                {r.barcode} · {r.brand}
              </p>
              {r.artNo && (
                <p className="font-mono-num text-[11px] text-muted-foreground">
                  art {r.artNo} · {r.color}{r.size && ` · sz ${r.size}`}
                </p>
              )}
            </div>
            <div className="text-right">
              <p className={cn(
                'font-mono-num text-base font-semibold',
                r.trantype === 'P' ? 'text-rose-deep' : 'text-ink',
              )}>
                {r.trantype === 'P' ? `-${fmtINR(r.sp)}` : fmtINR(r.sp)}
              </p>
              <p className="font-mono-num text-[10px] text-muted-foreground">
                {r.trantype === 'P' ? 'purchase' : 'sale'}
              </p>
            </div>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border/40 pt-2 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1">
              <User2 className="h-3 w-3" />
              {r.salesmanName || r.salesmanId || '—'}
              {r.posUser && <span className="text-muted-foreground/70">/{r.posUser}</span>}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" /> {fmtDate(r.date)} {r.time}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="font-display text-lg font-medium text-ink sm:text-xl">
      <span className="mr-3 inline-block h-px w-8 -translate-y-1.5 bg-copper align-middle" />
      {children}
    </h2>
  );
}

function Chip({ children, onRemove }: { children: React.ReactNode; onRemove: () => void }) {
  return (
    <button
      type="button" onClick={onRemove}
      className="inline-flex items-center gap-1 rounded-full border border-border/80 bg-sand px-2.5 py-1 text-[11px] font-medium text-ink transition-colors hover:border-copper hover:text-copper"
    >
      {children}
      <X className="h-3 w-3" />
    </button>
  );
}

function SkeletonHero() {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 sm:gap-6">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="space-y-2">
          <div className="h-3 w-16 rounded bg-muted animate-pulse" />
          <div className="h-10 w-24 rounded bg-muted animate-pulse" />
        </div>
      ))}
    </div>
  );
}
